import Constants from '@intercom/embercom-composer/lib/constants';
import UiSelection from '@intercom/embercom-composer/lib/editor/ui-selection';
import ImageBlock from '@intercom/embercom-composer/blocks/types/image';
import VideoFileBlock from '@intercom/embercom-composer/blocks/types/video-file';
import AttachmentBlock from '@intercom/embercom-composer/blocks/types/attachment';
import KeyboardEventHandling from '@intercom/embercom-composer/components/mixins/keyboard-event-handling';
import MouseEventHandling from '@intercom/embercom-composer/components/mixins/mouse-event-handling';
import CopyPasteEventHandling from '@intercom/embercom-composer/components/mixins/copy-paste-event-handling';
import FocusEventHandling from '@intercom/embercom-composer/components/mixins/focus-event-handling';
import DomMutationHandling from '@intercom/embercom-composer/components/mixins/dom-mutation-handling';
import DomSync from '@intercom/embercom-composer/components/mixins/dom-sync';
import { ternary } from '@intercom/pulse/lib/computed-properties';
import SelectionCoordinates from '@intercom/embercom-composer/components/mixins/selection-coordinates';
import HoverCoordinates from '@intercom/embercom-composer/components/mixins/hover-coordinates';
import Render from '@intercom/embercom-composer/lib/editor/renderer';
import textWithNewlines from '@intercom/embercom-composer/lib/text-with-newlines';
import VideoBlock from '@intercom/embercom-composer/blocks/types/video';
import { editorHasSubstantialContent, editorHasAnyContent } from '@intercom/embercom-composer/lib/editor/content-checker';
import $ from 'jquery';
import { alias, reads } from '@ember/object/computed';
import { bind, cancel, debounce, schedule, throttle } from '@ember/runloop';
import Em from 'ember';
export default Em.Component.extend(KeyboardEventHandling, MouseEventHandling, CopyPasteEventHandling, FocusEventHandling, DomMutationHandling, DomSync, SelectionCoordinates, HoverCoordinates, {
  tagName: 'div',
  top: 0,
  classNames: ['embercom-composer-editor'],
  classNameBindings: ['isSelecting:is-selecting', 'allowAlignment::composer-has-no-alignment'],
  attributeBindings: ['contenteditable', 'data-gramm'],
  contenteditable: ternary('config.readOnly', 'false', 'true'),
  'data-gramm': 'false',
  $editor: null,
  customTemplate: null,
  composerState: null,
  hasAnyContent: false,
  hasSubstantialContent: false,
  lastUpdated: alias('composerState.lastUpdated'),
  selectedBlock: alias('composerState.selection.firstSelectedBlock'),
  scrollOffset: 0,
  isScrolling: false,
  disableHoveredBlockUpdates: false,
  editorDimensions: undefined,
  currentEditableEntity: reads('composerState.currentEditableEntity'),
  currentEditableBlock: reads('composerState.currentEditableBlock'),
  allowAlignment: reads('config.allowAlignment'),
  setup: Em.on('init', function () {
    this.set('editorDimensions', Em.Object.create());
    this.set('uiSelection', UiSelection.create());
  }),
  onConfigChange: Em.observer({
    dependentKeys: ['config.name'],
    fn() {
      schedule('afterRender', this, this.syncAndUpdateDomAndSelection);
    },
    sync: true
  }),
  getUiSelection() {
    return this.get('uiSelection').getSelection(this.get('$editor'));
  },
  didInsertElement() {
    this._super(...arguments);
    let self = this;
    this.set('$editor', $(this.element));
    this.set('composerRenderer', new Render(this.get('$editor')));
    $(this.element).on('scroll', function () {
      self.set('scrollTimer', throttle(self, self.handleScroll, 10, false));
    });
    this.updateDomAndSelection();
    schedule('afterRender', this, function () {
      this._setEditorDimensionsAfterImageLoad();
      this.focusAndPosition();
      this.setEditorDimensions();
      this.initHoverCoordinates();
      this.checkForContent();
    });
    $(window, this.element).on('resize.embercom-composer', function () {
      throttle(this, this.setEditorDimensions, 50, false);
    }.bind(this));
  },
  willDestroyElement() {
    this._super(...arguments);
    $(window, this.element).off('resize.embercom-composer');
    $(this.element).off('scroll');
    this.set('$editor', null);
    this.set('$composer', null);
    this.set('composerRenderer', null);
    $('.embercom-composer img').off(`load.resize-editor-${this.get('elementId')}`);
    cancel(this.get('unsetAnchorTimer'));
    cancel(this.get('rangeSelectionInfoTimer'));
    cancel(this.get('scrollTimer'));
    cancel(this.get('scrollEndTimer'));
  },
  load(blocks, keepFocus) {
    if (!keepFocus) {
      this.removeFocus();
    }
    this.get('composerState').load(blocks);
    this._updateAfterLoadBlocks(keepFocus);
  },
  _updateAfterLoadBlocks(focus) {
    if (focus) {
      this.updateDomAndSelection();
    } else {
      this.updateDom();
    }
    this._setEditorDimensionsAfterImageLoad();
    schedule('afterRender', this, this.checkForContent);
  },
  recover(blocks) {
    this.get('uiSelection.domSelection').clear();
    this.get('$editor').empty();
    this.get('composerState').load(blocks, true);
    this.updateDom();
    this._setEditorDimensionsAfterImageLoad();
    schedule('afterRender', this, this.checkForContent);
  },
  sendError(error) {
    this.onError?.(error);
  },
  sendChange() {
    this.change();
  },
  setBlockSelection() {
    let currentSelection = this.getUiSelection();
    if (currentSelection) {
      this.get('composerState').setSelection(currentSelection.startBlockIndex, currentSelection.startItemIndex, currentSelection.startPositionIndex, currentSelection.endBlockIndex, currentSelection.endItemIndex, currentSelection.endPositionIndex);
    }
  },
  clearBlockSelection() {
    this.get('uiSelection').clearBlockSelection(this.get('$editor'));
  },
  clearSelection() {
    if (this.get('uiSelection.domSelection').selectionIsInEditor()) {
      this.get('$editor').blur();
      this.get('uiSelection').clearDomSelection();
      this.get('composerState').setSelection(0, 0, 0, 0, 0, 0);
      this.setSelectionCoordinates();
    }
  },
  _applyEntity(type) {
    let tagName = type.slice(0, 1).toUpperCase();
    if (this.get('uiSelection').isRange()) {
      this.syncCurrentBlockAndCheckpoint();
      this.get('composerState')[type]();
      this.updateDomAndSelection();
      return;
    }
    let currentNode = this.get('uiSelection').getDomSelection().anchorNode;
    let isInsideBold = currentNode && this.get('uiSelection.domSelection')._findAncestorNode(currentNode, tagName);
    if (isInsideBold) {
      this.syncCurrentBlockAndCheckpoint();
      this.get('composerState').setEntitySkip(type);
      this.get('uiSelection').splitEntity(tagName);
      this.markCurrentBlockAsSynced();
    } else {
      this.syncCurrentBlockAndCheckpoint();
      this.get('composerState')[type](true);
      this.updateDomAndSelection();
      this.markCurrentBlockAsSynced();
      this.get('uiSelection').forceIntoNextEntityOfType(tagName);
    }
  },
  insertAttribute(attribute) {
    this.syncLastActiveBlock();
    this.get('composerState').insertAttribute(attribute);
    if (attribute.get('noFallback')) {
      this.updateDomAndSelection();
    } else {
      this.updateDom();
    }
  },
  _calculateSelectionIndex(index) {
    let selection;
    if (!index) {
      if (this.get('$editor')) {
        selection = this.getUiSelection();
      }
      if (selection) {
        index = selection.startBlockIndex;
      } else {
        index = this.get('composerState.selection.startBlockIndex');
      }
      if (this.get('composerState.selection.firstSelectedBlock.isNotEmpty')) {
        index++;
      }
    }
    return index;
  },
  insertVideo(videoUrl) {
    let index = this._calculateSelectionIndex(0);
    let videoBlock = VideoBlock.create({
      config: this.get('config'),
      inputUrl: videoUrl
    });
    let options = {
      index,
      selectNewBlock: false
    };
    if (!videoBlock.get('isValid')) {
      throw new Error('The video url is invalid');
    }
    this.insertBlock(videoBlock, options);
  },
  fileDrop(files) {
    if (this.get('config.allowUploads')) {
      this.insertFiles(files, this.get('hoverCoordinates.insertionIndex'));
    } else {
      this.get('parentView').send('onFileDroppedOnEditor', files);
    }
  },
  insertFiles(files, index) {
    index = this._calculateSelectionIndex(index);
    files.forEach(function (file) {
      if (file.type.match(/^image\//)) {
        if (!this.get('config.allowImage')) {
          return;
        }
        this.uploadImages(file, index);
        index++;
      } else if (file.type.match(/^video\//)) {
        if (!this.get('config.allowVideoFile')) {
          return;
        }
        this.uploadVideos(file, index);
        index++;
      } else {
        if (!this.get('config.allowAttachments')) {
          return;
        }
        this.uploadAttachments(file, index);
      }
    }, this);
  },
  uploadAttachments(fileOrFiles, index) {
    this._uploadFiles(fileOrFiles, AttachmentBlock, this.get('config.allowedFileTypes'), index);
  },
  uploadImages(fileOrFiles, index) {
    this._uploadFiles(fileOrFiles, ImageBlock, this.get('config.allowedImageTypes'), index);
  },
  uploadVideos(fileOrFiles, index) {
    this._uploadFiles(fileOrFiles, VideoFileBlock, this.get('config.allowedVideoTypes'), index);
  },
  _uploadFiles(fileOrFiles, blockClass, allowedFiles, index = 0) {
    let filesArr = Array.isArray(fileOrFiles) ? fileOrFiles : [fileOrFiles];
    index = this._calculateSelectionIndex(index);
    filesArr.forEach(file => {
      if (!allowedFiles.includes(file.type) && !allowedFiles.includes('*')) {
        return;
      }
      let block = blockClass.create({
        config: this.get('config'),
        uploadManager: this.get('uploadManager'),
        file
      });
      let options = {
        index,
        selectNewBlock: false
      };
      this.insertBlock(block, options);
      index++;
    });
  },
  format(style) {
    this[style]();
  },
  createBlock(type, data) {
    this.syncLastActiveBlock();
    this.get('composerState').createBlock(type, data);
    this.updateDomAndSelection();
    this._setEditorDimensionsAfterImageLoad();
  },
  insertBlock(block, options) {
    this.syncLastActiveBlock();
    this.get('composerState').insertBlock(block, options);
    this.takeFocus();
    this.updateDomAndSelection();
    this._setEditorDimensionsAfterImageLoad();
  },
  insertBlocks(blocks) {
    this.syncLastActiveBlock();
    this.get('composerState').insertBlocks(blocks);
    this.takeFocus();
    this.updateDomAndSelection();
    this._setEditorDimensionsAfterImageLoad();
  },
  insertBlocksAtMarker(blocks, markerId) {
    this.syncLastActiveBlock();
    this.get('composerState').insertBlocksAtMarker({
      blocks,
      markerId
    });
    this.takeFocus();
    this.updateDomAndSelection();
    this._setEditorDimensionsAfterImageLoad();
  },
  insertParagraphAtHoveredIndex() {
    this.syncLastActiveBlock();
    let index = this.get('hoverCoordinates.insertionIndex');
    this.get('composerState').insertParagraphAtIndex(index);
    this.updateDomAndSelection();
  },
  handleScroll() {
    this.set('scrollOffset', $(this.element).scrollTop());
    if (!this.get('isScrolling')) {
      this.set('isScrolling', true);
    }
    this.set('scrollEndTimer', debounce(this, this.onEndScrolling, 50));
  },
  onEndScrolling() {
    this.set('isScrolling', false);
  },
  scrollToSelection() {
    let scrollContainerSelector = this.get('scrollContainerSelector');
    if (scrollContainerSelector) {
      this._scrollContainerToSelection(scrollContainerSelector);
    } else {
      this._scrollContainerToSelection('.embercom-composer-editor');
    }
  },
  checkForContent() {
    this.set('hasSubstantialContent', editorHasSubstantialContent(this.get('$editor')));
    this.set('hasAnyContent', editorHasAnyContent(this.get('$editor')));
  },
  _scrollContainerToSelection(containerSelector) {
    let margin = 30;
    let container = $(this.element).closest(containerSelector).get(0);
    if (!container) {
      return;
    }
    let containerBounds = container.getBoundingClientRect();
    let selectionBounds = this.get('uiSelection').getCoords(this.get('$editor'));
    if (!selectionBounds) {
      return;
    }
    let caretIsAbove = selectionBounds.y <= containerBounds.top + margin;
    let caretIsBelow = selectionBounds.y >= containerBounds.bottom - margin;
    if (caretIsAbove) {
      container.scrollTop += selectionBounds.y - containerBounds.top - margin;
    } else if (caretIsBelow) {
      container.scrollTop += selectionBounds.y - containerBounds.bottom + margin;
    }
  },
  setEditorDimensions: Em.observer({
    dependentKeys: ['customTemplate'],
    fn() {
      schedule('afterRender', this, function () {
        let $editor = this.get('$editor');
        if ($editor) {
          let editorOffset = $editor.offset();
          let editorPosition = $editor.position();
          let emailTemplateOffset = this.getEmailTemplateOffset($editor);
          this.get('editorDimensions').setProperties({
            top: editorOffset.top,
            left: editorOffset.left,
            height: this.get('$editor').outerHeight(true),
            width: this.get('$editor').outerWidth(true),
            positionTop: editorPosition.top,
            positionLeft: editorPosition.left,
            emailTemplateOffsetTop: emailTemplateOffset.top,
            emailTemplateOffsetLeft: emailTemplateOffset.left
          });
          this.setSelectionCoordinates();
          this._showEditorDebugOutlines();
        }
      });
    },
    sync: true
  }),
  getEmailTemplateOffset($editor) {
    let top = 0;
    let left = 0;
    let $emailTemplateComposer = $editor.closest('#composer-content');
    let $emailTemplateContainer = $editor.closest('.composer-template-container');
    if ($emailTemplateComposer.length && $emailTemplateContainer.length) {
      let containerOffset = $emailTemplateContainer.offset();
      let composerOffset = $emailTemplateComposer.offset();
      top = composerOffset.top - containerOffset.top;
      left = composerOffset.left - containerOffset.left;
    }
    return {
      top,
      left
    };
  },
  focusAndPosition(forceFocus, forceFocusAtTheEnd) {
    if (forceFocus) {
      this.focusEditor(forceFocusAtTheEnd);
    } else {
      this._maybeAutoFocus();
    }
    this.setEditorDimensions();
  },
  getClosestScrolledElement($element) {
    return $element.scrollTop() > 0 ? $element : $element.scrollParent();
  },
  updateDomIfNotFocused() {
    if (this._isNotFocused()) {
      this.updateDom();
    }
  },
  takeFocus() {
    //#1321 - Applying $(this.element).focus causes the browser to jump to the top of the composer.
    //We need to save the scroll of the closest ancestor with a scrollTop value greater than 0 and then reset the scroll position to that value.
    if (this._isNotFocused()) {
      let $scrolledElement = this.getClosestScrolledElement($(this.element));
      let top = $scrolledElement.scrollTop();
      $(this.element).focus();
      $scrolledElement.scrollTop(top);
    }
  },
  _isNotFocused() {
    return !this.get('$editor').is(':focus');
  },
  _maybeAutoFocus() {
    if (this.get('config').get('autoFocus') && $(this.element).length) {
      this.focusEditor();
    }
  },
  focusEditor(forceFocusAtTheEnd) {
    let blockIndex = 0;
    let itemIndex = 0;
    let positionIndex = 0;
    let composerState = this.get('composerState');
    $(this.element).focus();
    if (this.get('config').get('autoFocusBottomRight') || forceFocusAtTheEnd) {
      blockIndex = composerState.get('blockList.endBlockIndex');
      itemIndex = composerState.get('blockList.endItemIndex');
      positionIndex = composerState.get('blockList.endPositionIndex');
    }
    composerState.setSelection(blockIndex, itemIndex, positionIndex, blockIndex, itemIndex, positionIndex);
    this.get('uiSelection').updateSelection(this.get('composerState.selection'), this.get('$editor'));
  },
  updateHeightOnBlockChange: Em.observer({
    dependentKeys: ['blocks'],
    fn() {
      throttle(this, this._performHeightUpdateForBlockChange, 50, false);
    },
    sync: true
  }),
  _performHeightUpdateForBlockChange() {
    if (!this.get('isDestroying') && this.get('$editor')) {
      this.set('height', this.get('$editor').outerHeight(true));
    }
  },
  _setEditorDimensionsAfterImageLoad() {
    $('.embercom-composer img').on(`load.resize-editor-${this.get('elementId')}`, bind(this, this.setEditorDimensions));
  },
  _showEditorDebugOutlines() {
    if (this.get('showDebugOutlines') && !this.get('isDestroying')) {
      this.get('$editor').closest('#custom-template-debug-outline').css({
        top: 0,
        left: 0,
        width: this.get('editorDimensions.emailTemplateOffsetLeft'),
        height: this.get('editorDimensions.emailTemplateOffsetTop')
      });
    }
  },
  setTextBeforeCursor() {
    let selection = this.getUiSelection();
    let beforeCursor = '';
    if (selection) {
      let domText = textWithNewlines(this.getCurrentBlock(true));
      let cursorBlockText = domText.replace(Constants.ZERO_WIDTH_SPACE_GLOBAL_REGEX, '');
      beforeCursor = cursorBlockText.slice(0, selection.startPositionIndex);
    }
    if (this.get('textBeforeCursor') !== beforeCursor) {
      this.set('textBeforeCursor', beforeCursor);
    }
  },
  addSelectionBorderToCurrentBlock() {
    this.get('uiSelection').selectBlock(this.get('$editor'), this.get('composerState.selection.startBlockIndex'));
  }
});